<template>
  <div class="shot-page">

        <!-- Notifications -->
    <div v-if="notificationMessage" class="notice" :class="notificationType" role="status" aria-live="polite">
      <span>{{ notificationMessage }}</span>
      <button class="btn btn--ghost small" @click="clearNotification">Dismiss</button>
    </div>

    <!-- Header -->
    <header class="head">
      <div class="head__titles">
        <h1>Screenshot Viewer</h1>
        <p class="muted">Request #{{ requestID }}</p>
      </div>


      <!-- Toolbar (lean) -->
     <div class="toolbar" v-if="dataUrl">
  <button class="btn" @click="setZoom(1)" :disabled="!dataUrl"
          data-track="shot_zoom_100">100%</button>

  <div class="sep"></div>
      
  <button class="btn" @click="zoomOut" :disabled="!dataUrl"
          data-track="shot_zoom_out_click">−</button>
  <div class="zoom-readout">{{ Math.round(zoom*100) }}%</div>
  <button class="btn" @click="zoomIn" :disabled="!dataUrl"
          data-track="shot_zoom_in_click">+</button>
        <div class="sep"></div>
      <!-- <button class="btn" @click="rotate">Rotate 90°</button>--> 
      <div class="chip" v-if="metaText">{{ metaText }}</div>
<!--<div class="chip" v-if="showingHQ">HQ</div>
<div class="chip" v-else>LQ</div>-->
<div class="sep" v-if="hqAvailable"></div>
  <button v-if="hqAvailable && !showingHQ"
          class="btn"
          @click="viewHQ"
          :disabled="isLoading"
          data-track="shot_view_hq_click">
    View High Quality
  </button>
<button v-if="hqAvailable && showingHQ"
          class="btn"
          @click="closeHQ"
          :disabled="isLoading"
          data-track="shot_close_hq_click">
    Close High Quality
  </button>
<!-- HQ controls -->
<div v-if="hqAvailable && showingHQ" class="chip">{{ hqPrettySize }}</div>

<!-- If currently viewing HQ: show the HQ download -->
<button v-if="hqAvailable && showingHQ"
          class="btn btn--accent"
          @click="downloadHQ"
          :disabled="isLoading"
          data-track="shot_download_hq_click">
    ⬇︎ Download
  </button>
  <button v-else
          class="btn"
          @click="downloadImage"
          :disabled="isLoading || !dataUrl"
          data-track="shot_download_lq_click">
    ⬇︎ Download
  </button>

        <button class="btn" @click="refresh" :disabled="isLoading" data-track="shot_refresh_click">Refresh</button>

  <router-link class="btn btn--ghost"
               to="/AnalysisTool"
               data-track="shot_back_to_tool">
    Back
  </router-link>
      </div>
    </header>


    <!-- Viewer -->
    <section class="card viewer" ref="viewer">
      <div v-if="isLoading" class="skel skel--image"></div>

      <div v-else-if="!dataUrl" class="empty">
        No screenshot available for this request.
      </div>

      <div
        v-else
        class="canvas"
        ref="canvas"
        @wheel.prevent="onWheel" 
        @dblclick="toggleZoom"
        @mousedown="onDragStart"
      >
    <img
  :src="dataUrl"
  :key="dataUrl"
  alt="Screenshot"
  class="image"
  :style="imageStyle"
  @load="onImgLoad"
  draggable="false"
/>

      </div>
    </section>

    <!-- Legend -->
    <section class="card legend">
      <h2 class="legend__title">Flag Legend</h2>
      <ul class="legend__grid">
        <li class="legend__item">
          <img src="../assets/greenflag.png" alt="" class="flag" />
          <span>Funds likely went to an exchange</span>
        </li>
        <li class="legend__item">
          <img src="../assets/blueflag.png" alt="" class="flag" />
          <span>Funds remain at the address</span>
        </li>
        <li class="legend__item">
          <img src="../assets/orangeflag.png" alt="" class="flag" />
          <span>Complication — manual investigation required</span>
        </li>
        <li class="legend__item">
          <img src="../assets/redflag.png" alt="" class="flag" />
          <span>Unrecoverable (non-compliant exchanges, gambling)</span>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ViewImage',
  props: ['requestID'],
  data() {
    return {
      username: '',
      isLoading: false,

      // notifications
      notificationMessage: '',
      notificationType: 'info',

      // image sources
      dataUrl: '',
      base64Raw: '',
      mime: 'image/jpeg',

      // zoom / rotate
      zoom: 1,
      rotation: 0,
      naturalW: 0,
      naturalH: 0,

      // meta + filename stamp
      metaText: '',
      downloadStamp: '',

      // drag-to-pan
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      scrollStartLeft: 0,
      scrollStartTop: 0,


    hqAvailable: false,
    hqMime: '',
    hqBytes: 0,
    hqUrl: '',       // object URL created from Blob
    showingHQ: false,

  // NEW: cache the LQ image so we can return to it without refetching
    lqUrl: '',        // data URL for the low-quality image
    lqMime: '',       // detected mime for LQ
    // NEW:
    fitOnNextLoad: false,
     minZoom: 0.05, // will be set dynamically after image loads / on resize
    };
  },
  computed: {
     hqPrettySize() {
    const b = this.hqBytes || 0;
    if (b < 1024) return `${b} B`;
    if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/1024/1024).toFixed(2)} MB`;
  },
    imageStyle() {
      return {
         transform: `scale(${this.zoom}) rotate(${this.rotation}deg)`,
    transformOrigin: 'top left',
    display: 'block'
      };
    },
    fileExt() {
      return this.mime === 'image/png' ? 'png' : 'jpg';
    },
    downloadName() {
      const id = this.requestID || 'request';
      const stamp = this.downloadStamp || 'image';
      return `PathTracer_${id}_${stamp}.${this.fileExt}`;
    }
  },
  methods: {

   fitToViewport() {
  const canvas = this.$refs.canvas;
  if (!canvas || !this.naturalW || !this.naturalH) return;

  const vw = Math.max(1, canvas.clientWidth  - 6);
  const vh = Math.max(1, canvas.clientHeight - 6);
  const fit = Math.min(vw / this.naturalW, vh / this.naturalH);

  // allow zooming out below “fit” for huge images;
  // e.g., 25% of fit, but never lower than 1% and never higher than 0.2
  this.minZoom = Math.min(0.2, Math.max(0.01, fit * 0.25));

  // start at fit (show whole image)
  this.zoom = Math.max(this.minZoom, Math.min(6, fit));

  // center after zoom applies
  this.$nextTick(() => {
    const scaledW = this.naturalW * this.zoom;
    const scaledH = this.naturalH * this.zoom;
    const sx = Math.max(0, (scaledW - canvas.clientWidth)  / 2);
    const sy = Math.max(0, (scaledH - canvas.clientHeight) / 2);
    canvas.scrollLeft = sx;
    canvas.scrollTop  = sy;
  });
}

,
    preserveCenterOnZoom(prevZoom) {
  const el = this.$refs.canvas;
  if (!el || !this.naturalW || !this.naturalH) return;

  const ratio = this.zoom / prevZoom;
  // Keep the current center point stable
  const cx = el.scrollLeft + el.clientWidth / 2;
  const cy = el.scrollTop + el.clientHeight / 2;
  const nx = cx * ratio;
  const ny = cy * ratio;

  // Clamp to bounds
  el.scrollLeft = Math.max(0, Math.min(nx - el.clientWidth / 2, el.scrollWidth));
  el.scrollTop  = Math.max(0, Math.min(ny - el.clientHeight / 2, el.scrollHeight));
},
setZoom(z) {
  const prev = this.zoom;
  const floor = Math.max(0.01, this.minZoom || 0.01); // safety floor
  this.zoom = Math.min(6, Math.max(floor, z));
  if (prev !== this.zoom) this.$nextTick(() => this.preserveCenterOnZoom(prev));
}
,

   async viewHQ() {
  try {
    this.isLoading = true;
    const token = localStorage.getItem('token');
    const res = await axios.get('/user/hq_images/download.php', {
      params: { request_id: this.requestID },
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });

    if (this.hqUrl) URL.revokeObjectURL(this.hqUrl);
    this.hqUrl = URL.createObjectURL(res.data);

    this.dataUrl = this.hqUrl;
    this.mime = res.data.type || this.hqMime || 'image/png';
    this.showingHQ = true;

    // NEW: next image load should auto‑fit
    this.fitOnNextLoad = true;

    this.showNotification('High-quality image loaded.', 'info');
  } catch (e) {
    console.error('viewHQ error', e);
    this.showNotification('Could not load HQ image.', 'error');
  } finally {
    this.isLoading = false;
  }
},



closeHQ() {
  if (this.hqUrl) URL.revokeObjectURL(this.hqUrl);
  this.hqUrl = '';
  this.showingHQ = false;

  // Restore cached LQ immediately (no network)
  if (this.lqUrl) {
    this.dataUrl = this.lqUrl;
    this.mime = this.lqMime || 'image/jpeg';
  } else {
    // Fallback (shouldn’t happen often)
    this.fetchScreenshot();
  }
},

  async downloadHQ() {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/user/hq_images/download.php', {
        params: { request_id: this.requestID },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      const ext = (res.data.type === 'image/png') ? 'png' :
                  (res.data.type === 'image/webp') ? 'webp' :
                  (res.data.type === 'image/jpeg') ? 'jpg' : 'bin';
      a.href = url;
      a.download = `PathTracer_${this.requestID}_HQ.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('downloadHQ error', e);
      this.showNotification('Could not download HQ image.', 'error');
    }
  },

     async checkHQ() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      // You can hit meta.php directly to get size + mime in one call:
      const res = await axios.get('/user/hq_images/meta.php', {
        params: { request_id: this.requestID },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.available) {
        this.hqAvailable = true;
        this.hqMime = res.data.mime || '';
        this.hqBytes = res.data.byte_size || 0;
      } else {
        this.hqAvailable = false;
      }
    } catch (e) {
      console.warn('HQ meta check failed', e);
    }
  },
    storageKey(key) { return `shot:${this.requestID}:${key}`; },

  async fetchScreenshot() {
  this.isLoading = true;
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      this.showNotification('Please log in to view screenshots.', 'error');
      this.$router.push('/login');
      return;
    }

    const res = await axios.get('etherscan/retrieve_screenshot.php', {
      params: { request_id: this.requestID, username: this.username },
      headers: { Authorization: `Bearer ${token}` } // <-- REQUIRED
    });

    if (res.data?.status === 'success' && res.data.image_data) {
      const { dataUrl, mime } = this.prepareDataUrl(res.data.image_data);
      this.lqUrl = dataUrl; this.lqMime = mime;
      if (!this.showingHQ) { this.dataUrl = this.lqUrl; this.mime = this.lqMime; }
      this.showNotification('Screenshot loaded successfully.', 'info');

    } else {
      if (!this.showingHQ) this.dataUrl = '';
      this.showNotification(res.data?.message || 'No screenshot available.', 'error');
    }
  } catch (e) {
    // Handle auth fail gracefully
    if (e?.response?.status === 403) {
      this.showNotification('Your session expired. Please sign in again.', 'error');
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      this.$router.push('/login');
      return;
    }
    console.error('Error fetching screenshot:', e);
    this.showNotification('Error retrieving screenshot. Please try again later.', 'error');
  } finally {
    this.isLoading = false;
  }
},

    // Accept raw base64 or full data URL; guess MIME
  prepareDataUrl(image_data) {
  if (typeof image_data !== 'string') return { dataUrl: '', mime: '' };

  if (image_data.startsWith('data:')) {
    const m = image_data.match(/^data:([^;]+);base64,/i);
    const mime = m ? m[1] : 'image/jpeg';
    return { dataUrl: image_data, mime };
  } else {
    let raw = image_data.replace(/-/g, '+').replace(/_/g, '/');
    const pad = raw.length % 4;
    if (pad) raw += '='.repeat(4 - pad);

    // Detect by signature
    // JPEG: /9j/
    // PNG:  iVBOR
    // WEBP: UklG (RIFF/WEBP base64 can vary, this catches common lossy case)
    let mime = 'image/jpeg';
    if (raw.startsWith('iVBOR')) mime = 'image/png';
    else if (raw.startsWith('/9j/')) mime = 'image/jpeg';
    else if (raw.startsWith('UklG')) mime = 'image/webp';

    const dataUrl = `data:${mime};base64,${raw}`;
    return { dataUrl, mime };
  }
},
onImgLoad(e) {
  const img = e.target;
  this.naturalW = img.naturalWidth;
  this.naturalH = img.naturalHeight;
  this.metaText = `${this.naturalW}×${this.naturalH} · ${this.mime.replace('image/','').toUpperCase()}`;

  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  this.downloadStamp = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;

  // Always fit to viewport on load (both LQ and HQ)
  this.fitToViewport();
}
,

    // Download with custom filename (works across browsers)
    downloadImage() {
      try {
        const blob = this.dataUrlToBlob(this.dataUrl);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.downloadName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('downloadImage fallback', e);
        // Fallback: open in a new tab (user can Save As)
        window.open(this.dataUrl, '_blank', 'noopener');
      }
    },

    dataUrlToBlob(dataUrl) {
      const [head, b64] = dataUrl.split(',');
      const mimeMatch = head.match(/data:([^;]+);base64/i);
      const mime = mimeMatch ? mimeMatch[1] : this.mime || 'image/jpeg';
      const binStr = atob(b64);
      const len = binStr.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binStr.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    },

    // Zoom controls
    zoomIn()  { this.setZoom(this.zoom * 1.15); },
    zoomOut() { this.setZoom(this.zoom / 1.15); },

    toggleZoom() { this.setZoom(this.zoom < 1.01 ? 1.75 : 1); },

    onWheel(e) {
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      this.setZoom(this.zoom * factor);
    },

    // Rotate (kept, even if hidden in UI)
    rotate() {
      this.rotation = (this.rotation + 90) % 360;
    },

    // Drag-to-pan
    onDragStart(e) {
      const el = this.$refs.canvas;
      if (!el) return;
      this.dragging = true;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.scrollStartLeft = el.scrollLeft;
      this.scrollStartTop = el.scrollTop;
      window.addEventListener('mousemove', this.onDragMove);
      window.addEventListener('mouseup', this.onDragEnd);
      el.classList.add('dragging');
      e.preventDefault();
    },
    onDragMove(e) {
      if (!this.dragging) return;
      const el = this.$refs.canvas;
      if (!el) return;
      const dx = e.clientX - this.dragStartX;
      const dy = e.clientY - this.dragStartY;
      el.scrollLeft = this.scrollStartLeft - dx;
      el.scrollTop = this.scrollStartTop - dy;
    },
    onDragEnd() {
      this.dragging = false;
      const el = this.$refs.canvas;
      el && el.classList.remove('dragging');
      window.removeEventListener('mousemove', this.onDragMove);
      window.removeEventListener('mouseup', this.onDragEnd);
    },

    // Misc
    refresh() { this.fetchScreenshot(); },
    showNotification(message, type = 'info') {
      this.notificationMessage = message;
      this.notificationType = type;
    },
    clearNotification() {
      this.notificationMessage = '';
      this.notificationType = 'info';
    },

    // Keyboard shortcuts
  onKeydown(e) {
  const tag = (e.target && e.target.tagName) || '';
  if (['INPUT','TEXTAREA','SELECT'].includes(tag)) return;

  if (e.key === '+') this.zoomIn();
  else if (e.key === '-') this.zoomOut();
  else if (e.key === '0') this.setZoom(1);
  else if (e.key.toLowerCase() === 'r') this.rotate();
  else if (e.key.toLowerCase() === 'h' && this.hqAvailable) {
    if (!this.showingHQ) this.viewHQ();
    else this.closeHQ();
  }
},

  },
async   mounted() {
    this.username = localStorage.getItem('username');
    if (!this.username) {
      this.showNotification('You must be logged in to view this screenshot.', 'error');
      return;
    }
    if (!this.requestID) {
      this.showNotification('No request ID provided.', 'error');
      return;
    }



   await Promise.all([ this.fetchScreenshot(), this.checkHQ() ]);
  window.addEventListener('keydown', this.onKeydown);

   
  window.addEventListener('resize', this.fitToViewport);


  },
 beforeUnmount() {
  window.removeEventListener('keydown', this.onKeydown);
  this.onDragEnd();
  if (this.hqUrl) URL.revokeObjectURL(this.hqUrl);
  window.removeEventListener('resize', this.fitToViewport);
},

};
</script>

<style scoped>
/* Layout + theme */
.shot-page { max-width: 1120px; margin: 0 auto; padding: 1rem; color:#1f2a37; }
.head { display:flex; align-items:flex-end; justify-content:space-between; gap:.75rem; margin-bottom:.75rem; }
.head__titles h1 { margin:0; font-size:1.4rem; font-weight:800; color:#0f2740; }
.muted { color:#6b7280; }

/* Toolbar */
.toolbar {
  display:flex; align-items:center; gap:.45rem; flex-wrap:wrap;
  background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding:.35rem .45rem;
  box-shadow:0 6px 16px rgba(16,24,40,.06);
}
.sep { width:1px; height:24px; background:#e5e7eb; }
.zoom-readout { min-width:48px; text-align:center; color:#111827; font-weight:700; }
.chip { padding:.25rem .5rem; border-radius:999px; background:#eef2f7; color:#0f2740; font-weight:700; font-size:.85rem; }

/* Buttons */
.btn { padding:.45rem .65rem; border-radius:10px; border:1px solid rgba(15,38,61,.15);
  background:linear-gradient(180deg, #ffffff, #f3f4f6); cursor:pointer; font-weight:600; }
.btn:hover { background:linear-gradient(180deg,#f9fafb,#eef2f7); }
.btn:disabled { opacity:.6; cursor:not-allowed; }
.btn--ghost { background:#fff; }
.btn--accent { background:#0ea5e9; border-color:#0284c7; color:#fff; }
.btn--accent:hover { background:#0284c7; }
.small { font-size:.9rem; padding:.35rem .55rem; }

/* Cards */
.card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 10px 20px rgba(16,24,40,.06); }
.viewer {
  margin-top:.5rem;
  height: min(65vh, 600px); /* was 80vh/720px */
  position: relative;
  display: flex;
}

/* Canvas (checker background always on) */
.canvas {
  flex:1; overflow:auto; border-radius:12px; background:
    repeating-conic-gradient(#f9fafb 0% 25%, #eef2f7 0% 50%) 50% / 24px 24px;
  /* no centering while zooming—let scrollbars do their job */
  position: relative;
  cursor: grab;
}
.canvas.dragging { cursor: grabbing; }

.image { transform-origin: center center; max-width:none; max-height:none; user-select: none; }

/* Skeleton & empty */
.skel { background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%); background-size: 400% 100%; animation: pulse 1.2s ease-in-out infinite; border-radius: 12px; }
.skel--image { position:absolute; inset:12px; }
@keyframes pulse { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }
.empty { color:#6b7280; margin:auto; padding:1rem; }

/* Legend */
/* Legend */
.legend {
  margin-top: .75rem;
  padding: .75rem;           /* was 1rem */
}
.legend__title {
  margin: .25rem 0 .75rem;   /* less spacing above/below */
  font-size: 1rem;           /* was 1.1rem */
  font-weight: 700;          /* a bit lighter */
  color: #0f2740;
  text-align: center;
}
.legend__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(200px, 1fr)); /* slightly narrower */
  gap: .4rem .75rem;         /* tighter gaps */
  align-items: center;
  justify-items: start;
  padding: 0;
  margin: 0;
  list-style: none;
}
@media (max-width: 640px) {
  .legend__grid { grid-template-columns: 1fr; }
}
.legend__item {
  display: flex;
  align-items: center;
  gap: .4rem;                /* smaller gap between icon/text */
  font-size: .9rem;          /* shrink text a touch */
  line-height: 1.2;          /* tighter lines */
}
.flag {
  width: 18px;               /* was 22px */
  height: auto;
  display: block;
}

/* Notices */
.notice {
  display:flex; align-items:center; justify-content:space-between; gap:.5rem;
  margin:.5rem 0; padding:.6rem .75rem; border-radius:10px; font-weight:600;
}
.notice.info  { background:#e0f2fe; color:#0c4a6e; border:1px solid #bae6fd; }
.notice.error { background:#fee2e2; color:#7f1d1d; border:1px solid #fecaca; }
</style>
